import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import VideoSection, { ResourceSummaryItem } from './components/VideoSection';
import EvaluacionSection from './components/EvaluacionSection';
import RecursosSection from './components/RecursosSection';
import { useModuleProgress } from '../../hooks/useModuleProgress';
import { WorkshopVideo } from '../admin/components/WorkshopManager';

const LOGO = 'https://static.readdy.ai/image/1e1c36a8fe2eee6efab369c345d12000/d2de7d124fc44cc7dd03173e7e2df5a4.png';

type Tab = 'video' | 'recursos' | 'evaluacion' | 'mapa';

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'video', label: 'Video Introductorio', icon: 'ri-play-circle-line' },
  { id: 'recursos', label: 'Recursos', icon: 'ri-folder-open-line' },
  { id: 'evaluacion', label: 'Evaluación Inicial', icon: 'ri-questionnaire-line' },
  { id: 'mapa', label: 'Mapa del Trazado', icon: 'ri-map-2-line' },
];

interface ModuleContent {
  vimeo_id: string;
  ppt_embed_url: string;
  infographic_url: string;
  manual_url: string;
  workshop_videos: WorkshopVideo[];
  is_published: boolean;
}

interface QuizQuestion {
  id: string;
  question_text: string;
  options: string[];
  correct_index: number;
  order_index: number;
  option_explanations: string[];
}

function MapaTrazado() {
  const pillars = [
    { icon: 'ri-plant-line', name: 'Horizonte Arraigado', nodo: 1, desc: 'Propósito, valores y raíces identitarias', color: '#8B9D83', locked: true },
    { icon: 'ri-home-heart-line', name: 'Bio-Santuario', nodo: 2, desc: 'Entorno, cuerpo y espacio vital', color: '#C97B63', locked: true },
    { icon: 'ri-brain-line', name: 'Alquimia Mental', nodo: 3, desc: 'Patrones de pensamiento y arquitectura cognitiva', color: '#8B9D83', locked: true },
    { icon: 'ri-compass-3-line', name: 'Astrolabio Interior', nodo: 4, desc: 'Intuición, emoción y brújula interna', color: '#C97B63', locked: true },
    { icon: 'ri-shield-star-line', name: 'Maestría Multidimensional', nodo: 5, desc: 'Habilidades, expresión y legado', color: '#8B9D83', locked: true },
    { icon: 'ri-git-network-line', name: 'Plano Sistémico', nodo: 6, desc: 'Relaciones, economía y red de impacto', color: '#C97B63', locked: true },
  ];

  return (
    <div>
      {/* Center Nodo 0 */}
      <div className="text-center mb-10">
        <div className="inline-flex flex-col items-center gap-4">
          <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: 'rgba(139,157,131,0.12)', border: '2px solid rgba(139,157,131,0.5)', boxShadow: '0 0 40px rgba(139,157,131,0.15)' }}>
            <i className="ri-map-pin-2-line text-[#8B9D83] text-3xl" />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[3px] text-[#8B9D83] px-4 py-1.5 rounded-full" style={{ background: 'rgba(139,157,131,0.10)', border: '1px solid rgba(139,157,131,0.25)' }}>
              Nodo 0 · El Origen ✓
            </span>
          </div>
          <p className="text-[#F8F6F3]/40 text-sm max-w-sm">Tu punto de anclaje. El diagnóstico inicial que revela el estado actual de tu arquitectura interna.</p>
        </div>
      </div>

      {/* Connector line */}
      <div className="flex justify-center mb-6">
        <div className="w-px h-8" style={{ background: 'linear-gradient(to bottom, rgba(139,157,131,0.5), rgba(139,157,131,0.1))' }} />
      </div>

      {/* 6 Pillars Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pillars.map((p) => (
          <div
            key={p.nodo}
            className="rounded-2xl p-5 relative overflow-hidden opacity-50"
            style={{ background: 'rgba(248,246,243,0.03)', border: '1px solid rgba(248,246,243,0.06)' }}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${p.color}10` }}>
                <i className={`${p.icon} text-base`} style={{ color: p.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">Nodo {p.nodo}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-2-line text-[#F8F6F3]/20 text-xs" />
                  </div>
                </div>
                <p className="text-[#F8F6F3]/60 text-sm font-medium">{p.name}</p>
                <p className="text-[#F8F6F3]/30 text-xs mt-1 leading-relaxed">{p.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Unlock CTA */}
      <div className="mt-8 p-6 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, rgba(201,123,99,0.08) 0%, rgba(26,28,32,0.8) 100%)', border: '1px solid rgba(201,123,99,0.20)' }}>
        <i className="ri-lock-unlock-line text-[#C97B63] text-2xl mb-3 block" />
        <h3 className="text-[#F8F6F3] font-bold text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Desbloquea los 6 Nodos
        </h3>
        <p className="text-[#F8F6F3]/40 text-sm mb-6 max-w-md mx-auto">
          Activa el Blueprint Completo para acceder a la progresión secuencial de los 6 Pilares Sistémicos.
        </p>
        <a href="/dashboard" className="inline-flex items-center gap-2 bg-[#C97B63] text-[#F8F6F3] font-semibold text-sm px-8 py-3.5 rounded-xl hover:bg-[#b86d55] transition-all duration-300 cursor-pointer whitespace-nowrap">
          Ver Planes de Acceso
          <i className="ri-arrow-right-line" />
        </a>
      </div>
    </div>
  );
}

export default function Nodo0Page() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('video');
  const [moduleContent, setModuleContent] = useState<ModuleContent | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  const [activeResourceId, setActiveResourceId] = useState<string | null>(null);
  const { progress, markResourceOpened, markQuizPassed } = useModuleProgress('nodo0', user?.id);

  const handleGoToResource = (resourceId: string) => {
    setActiveResourceId(resourceId);
    setActiveTab('recursos');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [contentRes, quizRes] = await Promise.all([
          supabase.from('module_content').select('*').eq('module_id', 'nodo0').maybeSingle(),
          supabase.from('quiz_questions').select('*').eq('module_id', 'nodo0').order('order_index'),
        ]);
        if (contentRes.data) setModuleContent(contentRes.data as ModuleContent);
        if (quizRes.data) setQuizQuestions(quizRes.data as QuizQuestion[]);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const displayName = profile?.name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Nodo';

  const workshopVideos: WorkshopVideo[] = moduleContent?.workshop_videos || [];

  const requiredResourceIds = [
    moduleContent?.manual_url ? 'manual' : null,
    moduleContent?.ppt_embed_url ? 'ppt' : null,
    moduleContent?.infographic_url ? 'infografia' : null,
    ...workshopVideos.map((v) => `taller_${v.id}`),
  ].filter(Boolean) as string[];

  const allResourcesViewed =
    requiredResourceIds.length === 0 ||
    requiredResourceIds.every((id) => progress.openedResources.includes(id) || progress.quizPassed);

  const pendingResources = requiredResourceIds.filter(
    (id) => !progress.openedResources.includes(id) && !progress.quizPassed,
  );

  const resourceSummary: ResourceSummaryItem[] = [
    { id: 'manual', title: 'Manual del Nodo 0', icon: 'ri-file-pdf-line', color: '#C97B63', available: !!moduleContent?.manual_url, viewed: progress.openedResources.includes('manual') || progress.quizPassed },
    { id: 'ppt', title: 'Presentación del Módulo', icon: 'ri-slideshow-line', color: '#8B9D83', available: !!moduleContent?.ppt_embed_url, viewed: progress.openedResources.includes('ppt') || progress.quizPassed },
    { id: 'infografia', title: 'Infografía del Nodo 0', icon: 'ri-image-2-line', color: '#8B9D83', available: !!moduleContent?.infographic_url, viewed: progress.openedResources.includes('infografia') || progress.quizPassed },
    { id: 'glosario', title: 'Glosario Nodal', icon: 'ri-draft-line', color: '#C97B63', available: false, viewed: false },
    ...workshopVideos.map((v) => ({ id: `taller_${v.id}`, title: v.title || 'Ejercicio de Taller', icon: 'ri-play-circle-line', color: '#8B9D83', available: true, viewed: progress.openedResources.includes(`taller_${v.id}`) || progress.quizPassed })),
  ];

  return (
    <div
      className="min-h-screen bg-[#1A1C20]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(139,157,131,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,157,131,0.03) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}
    >
      {/* Top Bar */}
      <header className="border-b border-[#F8F6F3]/8 bg-[#1A1C20]/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-screen-xl mx-auto px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Nube Geodésica" className="h-8 w-auto object-contain" />
            <div className="w-px h-5 bg-[#F8F6F3]/10" />
            <nav className="flex items-center gap-2 text-[10px] uppercase tracking-[2px]">
              <a href="/dashboard" className="text-[#F8F6F3]/40 hover:text-[#8B9D83] transition-colors duration-200 cursor-pointer">
                Centro de Control
              </a>
              <span className="text-[#F8F6F3]/20">›</span>
              <span className="text-[#8B9D83]">Nodo 0</span>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#8B9D83] animate-pulse" />
              <span className="text-[#F8F6F3]/60 text-xs">Nodo activo</span>
            </div>
            <div className="w-px h-4 bg-[#F8F6F3]/10" />
            <span className="text-[#F8F6F3]/60 text-sm">{displayName}</span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-[#F8F6F3]/40 hover:text-[#C97B63] text-xs uppercase tracking-[1.5px] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line text-sm" /> Desconectar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-8 py-12">
        {/* Module Hero */}
        <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full" style={{ background: 'rgba(139,157,131,0.12)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.25)' }}>
                Nodo 0 · Desbloqueado
              </span>
              <span className="text-[10px] uppercase tracking-[2px] text-[#F8F6F3]/30">Módulo introductorio</span>
            </div>
            <h1 className="text-[#F8F6F3] font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.5px' }}>
              El Origen
            </h1>
            <p className="text-[#F8F6F3]/50 text-base leading-relaxed max-w-2xl">
              Bienvenido al umbral. Aquí comenzamos la Cartografía del Alma: un diagnóstico profundo del estado actual de tu arquitectura interna y el primer paso de tu Re-ingeniería Espiritual.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}>
              {[
                { icon: 'ri-play-circle-line', value: '28 min', label: 'Video' },
                { icon: 'ri-file-list-2-line', value: '4 PDFs', label: 'Recursos' },
                { icon: 'ri-questionnaire-line', value: `${quizQuestions.length || 0} preg.`, label: 'Quiz' },
              ].map((s) => (
                <div key={s.label} className="text-center px-4" style={{ borderRight: '1px solid rgba(248,246,243,0.06)' }}>
                  <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
                    <i className={`${s.icon} text-[#8B9D83] text-sm`} />
                  </div>
                  <p className="text-[#F8F6F3] text-sm font-semibold">{s.value}</p>
                  <p className="text-[#F8F6F3]/30 text-[10px]">{s.label}</p>
                </div>
              ))}
              <div className="text-center px-4">
                <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
                  <i className="ri-map-2-line text-[#8B9D83] text-sm" />
                </div>
                <p className="text-[#F8F6F3] text-sm font-semibold">1 Mapa</p>
                <p className="text-[#F8F6F3]/30 text-[10px]">Trazado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-2xl mb-10 flex-wrap" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.06)', width: 'fit-content' }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{
                background: activeTab === t.id ? 'rgba(139,157,131,0.15)' : 'transparent',
                color: activeTab === t.id ? '#8B9D83' : 'rgba(248,246,243,0.40)',
                border: activeTab === t.id ? '1px solid rgba(139,157,131,0.25)' : '1px solid transparent',
              }}
            >
              <i className={`${t.icon} text-sm`} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loadingData ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-[#8B9D83] text-2xl" />
          </div>
        ) : (
          <div>
            {activeTab === 'video' && <VideoSection vimeoId={moduleContent?.vimeo_id || ''} resourceSummary={resourceSummary} onGoToResource={handleGoToResource} />}
            {activeTab === 'recursos' && (
              <RecursosSection
                pptEmbedUrl={moduleContent?.ppt_embed_url || ''}
                infographicUrl={moduleContent?.infographic_url || ''}
                manualUrl={moduleContent?.manual_url || ''}
                workshopVideos={workshopVideos}
                openedResources={progress.openedResources}
                quizPassed={progress.quizPassed}
                activeResourceId={activeResourceId}
                onOpenResource={markResourceOpened}
              />
            )}
            {activeTab === 'evaluacion' && !allResourcesViewed && (
              <div className="flex flex-col items-center justify-center py-20 gap-6 max-w-md mx-auto text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,123,99,0.10)', border: '2px solid rgba(201,123,99,0.25)' }}>
                  <i className="ri-lock-2-line text-[#C97B63] text-3xl" />
                </div>
                <div>
                  <p className="text-[#C97B63] text-[10px] uppercase tracking-[3px] mb-2">Evaluación bloqueada</p>
                  <h3 className="text-[#F8F6F3] font-bold text-xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Consulta todos los recursos primero</h3>
                  <p className="text-[#F8F6F3]/45 text-sm leading-relaxed">Debes revisar todos los materiales del módulo antes de poder acceder a la evaluación.</p>
                </div>
                <div className="w-full p-4 rounded-2xl text-left" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}>
                  <p className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px] mb-3">Pendiente ({pendingResources.length})</p>
                  <div className="flex flex-col gap-2">
                    {pendingResources.map((id) => {
                      const res = resourceSummary.find((r) => r.id === id);
                      return res ? (
                        <div key={id} className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#C97B63', boxShadow: '0 0 5px rgba(201,123,99,0.5)' }} />
                          <span className="text-[#F8F6F3]/60 text-xs">{res.title}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
                <button onClick={() => setActiveTab('recursos')} className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90" style={{ background: '#8B9D83', color: '#1A1C20' }}>
                  <i className="ri-folder-open-line" /> Ir a Recursos
                </button>
              </div>
            )}
            {activeTab === 'evaluacion' && allResourcesViewed && <EvaluacionSection questions={quizQuestions} onQuizPassed={markQuizPassed} />}
            {activeTab === 'mapa' && <MapaTrazado />}
          </div>
        )}
      </main>
    </div>
  );
}
